import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface BillingStatus {
  subscription: {
    id: string;
    plan: string;
    status: string;
    trial_ends_at: string | null;
    current_period_end: string | null;
    canceled_at: string | null;
  };
  usage: {
    technicians: number;
    maxTechnicians: number;
  };
  recentPayments: Array<{
    id: string;
    amount_grosze: number;
    status: string;
    created_at: string;
  }>;
}

const PLAN_PRICES: Record<string, number> = {
  starter: 149,
  pro: 299,
  firma: 499,
};

const PLAN_LABELS: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  firma: "Firma",
};

function api(path: string, opts?: RequestInit) {
  const token = localStorage.getItem("kverna-token");
  return fetch(path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...opts?.headers,
    },
  });
}

export function Billing() {
  const { t } = useTranslation();
  const [data, setData] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [changingPlan, setChangingPlan] = useState(false);

  const load = async () => {
    const res = await api("/api/billing/status");
    if (res.ok) {
      setData(await res.json());
    } else {
      setError("Failed to load billing status");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSetupPayment = async () => {
    const res = await api("/api/billing/setup-payment", {
      method: "POST",
      body: JSON.stringify({ plan: data?.subscription.plan }),
    });
    if (res.ok) {
      const { paymentUrl } = await res.json();
      window.location.href = paymentUrl;
    } else {
      const err = await res.json();
      setError(err.error);
    }
  };

  const handleChangePlan = async (plan: string) => {
    setChangingPlan(true);
    const res = await api("/api/billing/change-plan", {
      method: "POST",
      body: JSON.stringify({ plan }),
    });
    if (res.ok) {
      await load();
    } else {
      const err = await res.json();
      setError(err.error);
    }
    setChangingPlan(false);
  };

  const handleCancel = async () => {
    if (!confirm(t("billing.confirmCancel"))) return;
    const res = await api("/api/billing/cancel", { method: "POST" });
    if (res.ok) {
      await load();
    } else {
      const err = await res.json();
      setError(err.error);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-navy-400">{t("common.loading")}</div>;
  }

  if (!data) {
    return <div className="text-center py-16 text-rose-500">{error || "Error"}</div>;
  }

  const sub = data.subscription;
  const isTrialing = sub.status === "trialing";
  const isActive = sub.status === "active";
  const isPastDue = sub.status === "past_due";
  const isCanceled = sub.status === "canceled";

  const trialDaysLeft = sub.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at).getTime() - Date.now()) / 86400000))
    : 0;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "var(--font-display)" }}>
        {t("billing.title")}
      </h1>
      <p className="text-navy-500 text-sm mb-8">{t("billing.subtitle")}</p>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Status card */}
      <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-navy-400">{t("billing.currentPlan")}</p>
            <p className="text-xl font-bold" style={{ fontFamily: "var(--font-display)" }}>
              {PLAN_LABELS[sub.plan] ?? sub.plan} — {PLAN_PRICES[sub.plan] ?? "?"} PLN/mies.
            </p>
          </div>
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full ${
              isActive ? "bg-emerald-50 text-emerald-600" :
              isTrialing ? "bg-sky-50 text-sky-600" :
              isPastDue ? "bg-amber-50 text-amber-600" :
              "bg-navy-100 text-navy-500"
            }`}
          >
            {isTrialing ? `Trial (${trialDaysLeft}d)` : sub.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-navy-400">{t("billing.technicians")}</p>
            <p className="font-medium">
              {data.usage.technicians} / {data.usage.maxTechnicians === Infinity ? "\u221e" : data.usage.maxTechnicians}
            </p>
          </div>
          <div>
            <p className="text-navy-400">
              {isTrialing ? t("billing.trialEnds") : sub.current_period_end ? t("billing.nextBilling") : ""}
            </p>
            <p className="font-medium">
              {isTrialing && sub.trial_ends_at
                ? new Date(sub.trial_ends_at).toLocaleDateString()
                : sub.current_period_end
                  ? new Date(sub.current_period_end).toLocaleDateString()
                  : "—"}
            </p>
          </div>
        </div>

        {(isTrialing || isPastDue) && (
          <button
            onClick={handleSetupPayment}
            className="btn btn-primary mt-6 w-full"
          >
            {t("billing.addPayment")}
          </button>
        )}

        {isActive && !isCanceled && (
          <button
            onClick={handleCancel}
            className="btn btn-ghost mt-6 text-rose-500 hover:bg-rose-50 border-rose-200"
          >
            {t("billing.cancel")}
          </button>
        )}

        {isCanceled && sub.current_period_end && (
          <p className="mt-4 text-sm text-navy-500">
            {t("billing.canceledInfo")} {new Date(sub.current_period_end).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Plan selection */}
      <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
        <h2 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>
          {t("billing.changePlan")}
        </h2>
        <div className="grid grid-cols-3 gap-3">
          {(["starter", "pro", "firma"] as const).map((plan) => (
            <button
              key={plan}
              onClick={() => handleChangePlan(plan)}
              disabled={plan === sub.plan || changingPlan}
              className={`rounded-xl border p-4 text-left transition-all ${
                plan === sub.plan
                  ? "border-amber-500 bg-amber-50"
                  : "border-navy-100 hover:border-navy-300"
              } ${changingPlan ? "opacity-50" : ""}`}
            >
              <p className="font-semibold text-sm">{PLAN_LABELS[plan]}</p>
              <p className="text-lg font-bold" style={{ fontFamily: "var(--font-display)" }}>
                {PLAN_PRICES[plan]} <span className="text-xs font-normal text-navy-400">PLN</span>
              </p>
              {plan === sub.plan && (
                <span className="text-[10px] text-amber-600 font-medium">{t("billing.currentLabel")}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Payment history */}
      {data.recentPayments.length > 0 && (
        <div className="bg-white rounded-xl border border-navy-100 p-6">
          <h2 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {t("billing.paymentHistory")}
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-navy-400 text-xs border-b border-navy-100">
                <th className="pb-2">{t("billing.date")}</th>
                <th className="pb-2">{t("billing.amount")}</th>
                <th className="pb-2">{t("billing.status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.recentPayments.map((p) => (
                <tr key={p.id} className="border-b border-navy-50">
                  <td className="py-2.5">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="py-2.5">{(p.amount_grosze / 100).toFixed(2)} PLN</td>
                  <td className="py-2.5">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        p.status === "succeeded" ? "bg-emerald-50 text-emerald-600" :
                        p.status === "pending" ? "bg-amber-50 text-amber-600" :
                        "bg-rose-50 text-rose-600"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
